"use client";

import type { AirQualityComponent, AirQualityUnit } from "@/db/definitions";
import { Link } from "@/i18n/routing";
import { useNearViewport } from "@/lib/hook/use-near-viewport";
import type { ListingAirQualityData } from "@/lib/types/air";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowUpRight,
  Frown,
  Info,
  Meh,
  Skull,
  Smile,
  ThumbsDown,
  Wind,
} from "lucide-react";
import { useTranslations } from "next-intl";
import React from "react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import AirComponentChart from "./ListingAirComponentChart";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AIR_QUALITY_COLORS,
  classifyAirQualityBand,
} from "@/lib/data/air-quality";
import type { AirQuality } from "@/lib/data/air-quality";
import CollapsibleSection from "../CollapsibleSection";

type Props = {
  geohash5: string;
};

const ListingAirQuality: React.FC<Props> = ({ geohash5 }) => {
  const t = useTranslations("listing.air-quality");
  const [containerRef, isNearViewport] = useNearViewport<HTMLDivElement>();
  const { data, isLoading } = useQuery({
    queryKey: ["listing", "air-quality", geohash5],
    enabled: isNearViewport,
    staleTime: 24 * 60 * 60 * 1000,
    retry: false,
    queryFn: async () => {
      const params = new URLSearchParams({ geohash5 });
      const response = await fetch(
        `/api/site/public/listing/air-quality?${params.toString()}`,
        { method: "GET" },
      );

      if (response.status === 404) {
        return [];
      }

      if (!response.ok) {
        throw new Error("Failed to fetch listing air quality");
      }

      const result = (await response.json()) as {
        success: boolean;
        data: ListingAirQualityData[];
      };

      return result.success ? result.data : [];
    },
  });

  const dataWithMeasurements = (data ?? []).filter(
    (item) => item.datapoints?.length,
  );

  return (
    <div ref={containerRef} className="min-h-px">
      {isNearViewport && isLoading && (
        <Skeleton className="h-[76px] w-full rounded-2xl" />
      )}

      {dataWithMeasurements.length > 0 && (
        <CollapsibleSection
          title={t("title")}
          subtitle={t("description")}
          icon={<Wind className="size-4" />}
        >
          <div className="space-y-2">
            <p className="pb-1 text-sm leading-relaxed text-muted-foreground sm:hidden">
              {t("description")}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
              {dataWithMeasurements
                .sort((a, b) => a.component.localeCompare(b.component))
                .map((d) => {
                  const lastMeasurement = d.datapoints![0];
                  const quality = classifyAirQualityBand(
                    d.component,
                    lastMeasurement?.value ?? 0,
                  );
                  const Icon = QUALITY_ICONS[quality];

                  return (
                    <div
                      key={d.component}
                      className="flex flex-col gap-1 border-input border bg-background rounded-lg p-2 sm:p-3"
                    >
                      <div className="flex flex-row gap-2 justify-between items-center">
                        <div className="flex flex-row gap-2 items-center">
                          <p className="text-sm sm:text-base font-semibold">
                            {componentText[d.component]}
                          </p>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger className="text-muted-foreground">
                                <Info className="h-3.5 w-3.5" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="max-w-xs text-xs leading-relaxed">
                                  {t(`component.${d.component}`)}
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>

                        <Badge
                          style={{
                            backgroundColor: AIR_QUALITY_COLORS[quality].bg,
                          }}
                        >
                          <Icon className="w-3.5 h-3.5 mr-2" />
                          {t(`quality.${quality}`)}
                        </Badge>
                      </div>

                      <div className="flex flex-row gap-4 items-center">
                        <p className="text-2xl font-bold whitespace-nowrap">
                          {lastMeasurement.value}
                          <span className="text-sm">
                            {unitText[lastMeasurement.unit]}
                          </span>
                        </p>
                        <div className="w-full">
                          <AirComponentChart
                            color={AIR_QUALITY_COLORS[quality].bg}
                            values={d.datapoints?.map((dp) => dp.value) ?? []}
                            height={50}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>

            <p className="text-xs text-muted-foreground leading-relaxed italic">
              {t.rich("link")}
              <Link
                href="https://www.luchtmeetnet.nl/"
                className="underline hover:text-primary transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span>Luchtmeetnet</span>
                <ArrowUpRight className="inline w-3 h-3 ml-1 mb-0.5" />
              </Link>
            </p>
          </div>
        </CollapsibleSection>
      )}
    </div>
  );
};

export default ListingAirQuality;

export const QUALITY_ICONS: Record<AirQuality, React.ComponentType<any>> = {
  GOOD: Smile,
  MODERATE: Meh,
  INSUFFICIENT: Frown,
  BAD: ThumbsDown,
  VERY_BAD: Skull,
};

const unitText: Record<AirQualityUnit, React.ReactNode> = {
  INDEX: <></>,
  UG_M3: (
    <>
      µg/m <sub>3</sub>
    </>
  ),
};

const componentText: Record<AirQualityComponent, React.ReactNode> = {
  NO_2: (
    <>
      NO<sub>2</sub>
    </>
  ),
  O_3: (
    <>
      O<sub>3</sub>
    </>
  ),
  PM_10: (
    <>
      PM<sub>10</sub>
    </>
  ),
  PM_2_5: (
    <>
      PM<sub>2.5</sub>
    </>
  ),
  LKI: <>Luchtkwaliteitsindex (LKI)</>,
};
